import { computed, toValue, type Ref, type ComputedRef } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import { useApi } from '@shopexperts/shared';

/**
 * Composable for searching experts
 * Uses the admin quickSearch API endpoint with built-in debouncing via staleTime
 */
export function useExpertSearch(searchQuery: Ref<string> | ComputedRef<string>) {
  const api = useApi();

  const searchValue = computed(() => toValue(searchQuery));
  const shouldSearch = computed(() => searchValue.value.length >= 2);

  const { data, isLoading, error } = useQuery({
    queryKey: computed(() => ['admin', 'experts', 'quickSearch', searchValue.value]),
    queryFn: async () => {
        console.log(`[useExpertSearch] Searching for ${searchValue.value}`);
      const response = await api.admin.expert.quickSearch({
        query: {
          query: searchValue.value
        }
      });
      console.log(`[useExpertSearch] Response:`, response);
      return response;
    },
    throwOnError: (error) => {
      console.error('[useExpertSearch] Error:', error);
      return false;
    },
    enabled: shouldSearch,
    staleTime: 30000, // 30 seconds - also helps with debouncing
    gcTime: 60000, // 1 minute
    // Debounce effect: queries are batched and refetching happens
    // only when user stops typing for the query to "settle"
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const isSearching = computed(() => isLoading.value);
  const searchError = computed(() => error.value);

  const hasResults = computed(() => {
    const results = data.value;
    return results && results.length > 0;
  });

  return {
    data,
    isSearching,
    searchError,
    hasResults,
  };
}


